import { useAuth } from '@/auth/useAuth';
import { ROLES } from '@/constants/roles';

import { useMyProfile } from '../hooks/useMyProfile';
import { useSyncSessionAvatar } from '../hooks/useSyncSessionAvatar';

/**
 * Keeps the app-wide avatar state (`AvatarProvider`) populated for the whole session, from anywhere
 * in the app. Renders nothing — it exists purely to own the query that generic chrome must not.
 *
 * WHY THIS IS APP-WIDE AND NOT PER-PAGE:
 * `SessionAvatar` is mounted by generic chrome (`TopNav`) and by the account page
 * (`features/identity/components/ProfileCard`), neither of which may import this feature's hooks
 * (FrontendGuidelines.md §3 — feature queries live in `features/*&#47;graphql`, and `components/` is
 * presentation-only). The photo, however, lives on the candidate's `TalentProfile`. Previously the
 * only writer was `ProfileBoundary`, which mounts on `/candidate/*` pages only — so landing first on
 * `/account/profile` (exactly where the TopNav account menu's "My Profile" goes) left the context at
 * its initial `null` and silently fell back to initials, with nothing to refetch or invalidate. The
 * upload had worked; the page simply never asked for the photo.
 *
 * Mounted once in `AppProviders` beside `WorkspaceProvider`, whose precedent this follows: a
 * feature-owned loader hoisted app-wide so shared chrome reads one cached result instead of each
 * screen re-fetching. Because it never unmounts on navigation, this costs one query per session, and
 * it shares `MY_PROFILE_QUERY`'s normalized cache entry with the candidate pages — so a photo upload
 * that refetches there updates this avatar too, with no page refresh.
 */
export function SessionAvatarSync(): null {
  const { user, isAuthenticated } = useAuth();

  // Only the Candidate role has a TalentProfile. Other roles have no photo to show, so querying
  // would be a guaranteed failure rather than an empty result.
  const isCandidate = Boolean(user?.roles.includes(ROLES.Candidate));
  const { profile } = useMyProfile({ skip: !isAuthenticated || !isCandidate });

  useSyncSessionAvatar(profile?.profilePhotoUrl ?? null);

  return null;
}
